import React, { useState } from "react";
import { Loader2, Store, Clock, Phone, Power, Images, Trash2, ArrowUp, ArrowDown, Plus, PlayCircle, MapPin, ReceiptText, ShieldCheck, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { PageHeader, LoadingBlock } from "../../components/app/ui";
import ImageField from "../../components/app/ImageField";
import RestaurantProfileForm from "../../components/app/RestaurantProfileForm";
import CuisineSelect from "../../components/app/CuisineSelect";
import LocationPicker from "../../components/app/LocationPicker";
import KycStatus from "../shared/KycStatus";
import { useMyRestaurant } from "./RestaurantOrders";
import { restaurantApi, errorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];
const DEFAULT_DAY = { open: "09:00", close: "22:00", closed: false };

function defaultHours(h) {
  const out = {};
  DAYS.forEach((d) => { out[d.key] = { ...DEFAULT_DAY, ...(h?.[d.key] || {}) }; });
  return out;
}

export default function RestaurantInfo() {
  const { user } = useAuth();
  const perms = user?.permissions;
  const orderOnlyManager = !!user?.manager_of && Array.isArray(perms) && perms.length > 0 && perms.every((p) => p === "orders");
  const [restaurant, setRestaurant] = useMyRestaurant();
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [form, setForm] = useState(null);
  const [menu, setMenu] = useState([]);
  const [photoUrl, setPhotoUrl] = useState("");
  const verified = (user?.verification_status === "verified") || (restaurant?.verification_status === "verified");
  const [addrReq, setAddrReq] = useState(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrBusy, setAddrBusy] = useState(false);
  const [addrForm, setAddrForm] = useState({ new_address: "", new_lat: null, new_lng: null, reason: "", proof_url: "" });

  React.useEffect(() => {
    if (verified) restaurantApi.addressChange().then(setAddrReq).catch(() => {});
  }, [verified]);

  const openAddrDialog = () => {
    setAddrForm({ new_address: restaurant.address || "", new_lat: restaurant.lat, new_lng: restaurant.lng, reason: "", proof_url: "" });
    setAddrOpen(true);
  };
  const submitAddr = async () => {
    if (!addrForm.new_address.trim() || !addrForm.reason.trim() || !addrForm.proof_url) {
      toast.error("Please set the new address, a reason and upload a proof document.");
      return;
    }
    setAddrBusy(true);
    try {
      const req = await restaurantApi.requestAddressChange({
        new_address: addrForm.new_address.trim(),
        new_lat: Number(addrForm.new_lat), new_lng: Number(addrForm.new_lng),
        reason: addrForm.reason.trim(), proof_url: addrForm.proof_url,
      });
      setAddrReq(req); setAddrOpen(false);
      toast.success("Address change submitted — our team will review it shortly.");
    } catch (e) { toast.error(errorMessage(e, "Could not submit request")); } finally { setAddrBusy(false); }
  };
  const cancelAddr = async () => {
    try { await restaurantApi.cancelAddressChange(); setAddrReq((r) => ({ ...r, status: "cancelled" })); toast.success("Request cancelled"); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  React.useEffect(() => {
    restaurantApi.menu().then(setMenu).catch(() => setMenu([]));
  }, []);

  React.useEffect(() => {
    if (restaurant) {
      setForm({
        name: restaurant.name || "", cuisines: restaurant.cuisines || (restaurant.cuisine ? [restaurant.cuisine] : []), address: restaurant.address || "",
        description: restaurant.description || "", image: restaurant.image || "",
        delivery_fee: restaurant.delivery_fee ?? 30, min_order: restaurant.min_order ?? 99,
        contact_phone: restaurant.contact_phone || "", contact_email: restaurant.contact_email || "",
        auto_schedule: !!restaurant.auto_schedule, hours: defaultHours(restaurant.hours),
        is_open: restaurant.is_open ?? true,
        gstin: restaurant.gstin || "", legal_name: restaurant.legal_name || "",
        billing_address: restaurant.billing_address || "", gst_registered: !!restaurant.gst_registered,
        logo_url: restaurant.logo_url || "",
      });
    }
  }, [restaurant]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setDay = (day, k, v) => setForm((f) => ({ ...f, hours: { ...f.hours, [day]: { ...f.hours[day], [k]: v } } }));
  const copyToAllDays = () => setForm((f) => {
    const base = f.hours?.mon || DEFAULT_DAY;
    const hours = {};
    DAYS.forEach((d) => { hours[d.key] = { ...base }; });
    return { ...f, hours };
  });
  const closeAllDays = () => setForm((f) => {
    const hours = {};
    DAYS.forEach((d) => { hours[d.key] = { ...(f.hours?.[d.key] || DEFAULT_DAY), closed: true }; });
    return { ...f, hours };
  });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const saved = await restaurantApi.save({
        name: form.name, cuisines: form.cuisines, address: form.address,
        description: form.description || null, image: form.image || null,
        delivery_fee: Number(form.delivery_fee), min_order: Number(form.min_order),
        lat: Number(form.lat), lng: Number(form.lng),
        contact_phone: form.contact_phone || null, contact_email: form.contact_email || null,
        auto_schedule: !!form.auto_schedule, hours: form.hours,
        categories: restaurant.categories || [],
        is_open: restaurant.is_open ?? true,
        gstin: (form.gstin || "").trim().toUpperCase() || null,
        legal_name: (form.legal_name || "").trim() || null,
        billing_address: (form.billing_address || "").trim() || null,
        gst_registered: !!form.gst_registered,
        logo_url: form.logo_url || null,
      });
      setRestaurant(saved);
      toast.success("Restaurant info saved");
    } catch (err) { toast.error(errorMessage(err, "Could not save")); } finally { setBusy(false); }
  };

  const addPhoto = async (url) => {
    if (!url) return;
    const current = restaurant.gallery || [];
    if (current.includes(url)) { toast("That photo is already in your gallery"); return; }
    try { const gallery = await restaurantApi.gallerySet([...current, url]); setRestaurant((r) => ({ ...r, gallery })); toast.success("Photo added"); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const removePhoto = async (url) => {
    try { const gallery = await restaurantApi.gallerySet((restaurant.gallery || []).filter((g) => g !== url)); setRestaurant((r) => ({ ...r, gallery })); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const movePhoto = async (idx, dir) => {
    const list = [...(restaurant.gallery || [])];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    setRestaurant((r) => ({ ...r, gallery: list })); // optimistic
    try { const gallery = await restaurantApi.gallerySet(list); setRestaurant((r) => ({ ...r, gallery })); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const toggleOpen = async () => {
    setToggling(true);
    try {
      const saved = await restaurantApi.setStatus(!restaurant.is_open);
      setRestaurant(saved);
      toast.success(saved.is_open ? "You're online" : "You're offline");
    } catch (err) { toast.error(errorMessage(err)); } finally { setToggling(false); }
  };

  const [pauseBusy, setPauseBusy] = useState(false);

  const resumeNow = async () => {
    setPauseBusy(true);
    try { const saved = await restaurantApi.resume(); setRestaurant(saved); toast.success("You're back online"); }
    catch (err) { toast.error(errorMessage(err)); } finally { setPauseBusy(false); }
  };

  if (restaurant === undefined || (restaurant && !form)) return <><PageHeader title="Restaurant info" /><LoadingBlock rows={4} /></>;
  if (restaurant === null) return <><PageHeader title="Set up your restaurant" description="Create your restaurant profile first." /><div className="max-w-3xl"><RestaurantProfileForm onSaved={setRestaurant} title="Create your restaurant" /></div></>;

  const openNow = restaurant.open_now;
  const accepting = restaurant.accepting_orders;

  return (
    <>
      <PageHeader title="Restaurant info & KYC" description="Your details, contact, opening hours and verification documents." actions={
        <div className={`flex items-center gap-3 rounded-full border px-4 py-2 ${restaurant.is_open ? "border-graphite-200 bg-graphite-50" : "border-surface-300 bg-white"}`} data-testid="online-toggle-wrap">
          <Power className={`h-4 w-4 ${restaurant.is_open ? "text-graphite" : "text-ink-muted"}`} />
          <span className="text-sm font-semibold text-ink">{restaurant.is_open ? "Online" : "Offline"}</span>
          <Switch checked={!!restaurant.is_open} disabled={toggling} onCheckedChange={toggleOpen} data-testid="restaurant-online-toggle" />
        </div>
      } />

      {(() => { const verified = (user?.verification_status === "verified") || (restaurant.verification_status === "verified"); return (
        <div className={`mb-6 flex items-center gap-3 rounded-2xl border px-5 py-3 ${verified ? "border-green-200 bg-green-50 text-green-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="profile-complete-banner">
          <ShieldCheck className="h-5 w-5" />
          <p className="text-sm font-semibold">{verified ? "Profile complete — your restaurant is verified and live." : "Verification pending — complete your details and KYC below to go live."}</p>
        </div>
      ); })()}

      <div className={`mb-6 flex flex-wrap items-center gap-3 rounded-2xl border px-5 py-3 ${accepting ? "border-graphite-200 bg-graphite-50 text-graphite" : restaurant.paused ? "border-amber-200 bg-amber-50 text-amber-800" : "border-brand-200 bg-brand-50 text-brand"}`} data-testid="open-now-banner">
        <Clock className="h-4 w-4" />
        <p className="text-sm font-medium">{restaurant.closed_message || (accepting ? "Open now — accepting orders." : "Closed — customers can't order right now.")}{form.auto_schedule && accepting ? " (Auto by opening hours)" : ""}</p>
        <div className="ml-auto flex items-center gap-2">
          {restaurant.closed_reason === "paused" ? (
            <Button type="button" size="sm" onClick={resumeNow} disabled={pauseBusy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="resume-now"><PlayCircle className="mr-1.5 h-4 w-4" /> Resume now</Button>
          ) : null}
        </div>
      </div>

      <form onSubmit={save} className="space-y-6">
        <div className="kt-card p-6">
          <div className="mb-5 flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand"><Store className="h-5 w-5" /></span><h2 className="text-lg font-semibold text-ink">Business details</h2></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label>Restaurant name</Label><Input required value={form.name} onChange={set("name")} data-testid="info-name" /></div>
            <div className="sm:col-span-2"><CuisineSelect value={form.cuisines} onChange={(v) => setForm((f) => ({ ...f, cuisines: v }))} /></div>
            {verified ? (
              <div className="space-y-2 sm:col-span-2" data-testid="address-locked">
                <Label>Address &amp; location</Label>
                <div className="rounded-xl border border-surface-300 bg-surface-50 px-4 py-3">
                  <p className="flex items-start gap-2 text-sm font-medium text-ink"><Lock className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" /> {restaurant.address}</p>
                  <p className="mt-1 pl-6 text-xs text-ink-soft">Pin: {Number(restaurant.lat).toFixed(5)}, {Number(restaurant.lng).toFixed(5)}</p>
                  <p className="mt-2 pl-6 text-xs text-ink-soft">Your address is locked after verification. To move your restaurant, request a change and our team will review it. Your current address stays live until it's approved.</p>
                  {addrReq?.status === "pending" ? (
                    <div className="mt-3 ml-6 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="address-change-pending">
                      Address change awaiting staff approval: <span className="font-semibold">{addrReq.new_address}</span>
                      <button type="button" onClick={cancelAddr} className="ml-2 font-semibold underline" data-testid="address-change-cancel">Cancel</button>
                    </div>
                  ) : (
                    <div className="mt-3 pl-6">
                      {addrReq?.status === "rejected" && <p className="mb-2 text-xs text-red-600" data-testid="address-change-rejected">Your last request wasn't approved{addrReq.reject_reason ? `: ${addrReq.reject_reason}` : ""}.</p>}
                      <Button type="button" variant="outline" className="rounded-full" onClick={openAddrDialog} data-testid="request-address-change"><MapPin className="mr-1.5 h-4 w-4" /> Request address change</Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
            <>
            <div className="space-y-2 sm:col-span-2"><Label>Address</Label><Input required value={form.address} onChange={set("address")} data-testid="info-address" /></div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Exact location on map</Label>
              <p className="-mt-1 text-xs text-ink-soft">Search your address, then drag the pin to your exact doorstep. Picking a place fills in your address automatically — tap <span className="font-semibold">Save changes</span> to apply it everywhere.</p>
              {(!restaurant.lat || (Number(form.lat).toFixed(3) === "13.083")) && (
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="coords-nudge">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>Please drop your exact pin below. Without it, customers can't see accurate distances or delivery times.</span>
                </div>
              )}
              <LocationPicker
                value={{ lat: Number(form.lat), lng: Number(form.lng), address: form.address }}
                onChange={({ lat, lng, address }) => setForm((f) => ({ ...f, lat, lng, address: address || f.address }))}
              />
            </div>
            </>
            )}
            <div className="space-y-2 sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={set("description")} data-testid="info-description" /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Cover image</Label><ImageField value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} testId="info-image" /></div>
            <div className="space-y-2"><Label>Minimum order (INR)</Label><Input type="number" min="0" value={form.min_order} onChange={set("min_order")} data-testid="info-min" /></div>
            <div className="space-y-2 sm:col-span-2 rounded-xl bg-surface-100 p-3">
              <p className="text-sm font-medium text-ink">Delivery area</p>
              <p className="text-xs text-ink-soft">Food delivery is available within <span className="font-semibold">10 km</span> of your restaurant for all partners. The delivery fee is calculated automatically by distance (₹50 up to 4 km, ₹70 up to 7 km, then +₹10/km). You don't need to set this.</p>
            </div>
          </div>
        </div>

        <div className="kt-card p-6">
          <div className="mb-5 flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand"><ReceiptText className="h-5 w-5" /></span><div><h2 className="text-lg font-semibold text-ink">Tax & billing</h2><p className="text-sm text-ink-soft">Prints on your customers' invoices for compliant records.</p></div></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2"><Label>Registered business / legal name</Label><Input value={form.legal_name} onChange={set("legal_name")} placeholder="e.g. Dimapur Diner Pvt Ltd" data-testid="info-legal-name" /><p className="text-xs text-ink-soft">The invoice will bill from this name instead of your personal name.</p></div>
            <label className="flex items-center justify-between rounded-xl bg-surface-100 px-4 py-3 sm:col-span-2">
              <div><p className="font-medium text-ink">GST registered</p><p className="text-xs text-ink-soft">5% GST (CGST 2.5% + SGST 2.5%) is charged on every order. Turn on to also show your GSTIN on invoices.</p></div>
              <Switch checked={form.gst_registered} onCheckedChange={(v) => setForm((f) => ({ ...f, gst_registered: v }))} data-testid="info-gst-registered" />
            </label>
            {form.gst_registered && <div className="space-y-2 sm:col-span-2"><Label>GSTIN</Label><Input value={form.gstin} onChange={set("gstin")} placeholder="13ABCDE1234F1Z5" maxLength={15} className="uppercase" data-testid="info-gstin" /><p className="text-xs text-ink-soft">15-character GST identification number.</p></div>}
            <div className="space-y-2 sm:col-span-2"><Label>Billing address</Label><Textarea rows={2} value={form.billing_address} onChange={set("billing_address")} placeholder="Full registered address (prints under the GSTIN)" data-testid="info-billing-address" /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Business logo</Label><ImageField value={form.logo_url} onChange={(v) => setForm((f) => ({ ...f, logo_url: v }))} testId="info-logo" /><p className="text-xs text-ink-soft">Printed on your invoices alongside the KohimaTaxi & Delivery mark.</p></div>
          </div>
        </div>

        <div className="kt-card p-6">
          <div className="mb-5 flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand"><Phone className="h-5 w-5" /></span><div><h2 className="text-lg font-semibold text-ink">Contact</h2><p className="text-sm text-ink-soft">Shown to customers so they can reach you.</p></div></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label>Contact phone</Label><Input value={form.contact_phone} onChange={set("contact_phone")} placeholder="+91…" data-testid="info-phone" /></div>
            <div className="space-y-2"><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={set("contact_email")} placeholder="hello@restaurant.com" data-testid="info-email" /></div>
          </div>
        </div>

        <div className="kt-card p-6">
          <div className="mb-5 flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand"><Clock className="h-5 w-5" /></span><div className="flex-1"><h2 className="text-lg font-semibold text-ink">Opening hours</h2><p className="text-sm text-ink-soft">Auto open/close your store by schedule.</p></div><div className="flex flex-wrap items-center gap-2"><button type="button" onClick={copyToAllDays} className="rounded-full border border-brand px-3 py-1.5 text-xs font-bold text-brand transition-colors hover:bg-brand-50" data-testid="hours-copy-all">Copy Monday to all days</button><button type="button" onClick={closeAllDays} className="rounded-full border border-surface-300 px-3 py-1.5 text-xs font-bold text-ink-soft transition-colors hover:bg-surface-100" data-testid="hours-close-all">Mark all days closed</button></div></div>
          <label className="mb-4 flex items-center justify-between rounded-xl bg-surface-100 px-4 py-3">
            <div><p className="text-sm font-medium text-ink">Auto schedule</p><p className="text-xs text-ink-soft">When on, you go online/offline automatically by the hours below (while you're Online).</p></div>
            <Switch checked={!!form.auto_schedule} onCheckedChange={(v) => setForm((f) => ({ ...f, auto_schedule: v }))} data-testid="info-auto-schedule" />
          </label>
          <div className="space-y-2">
            {DAYS.map((d) => {
              const day = form.hours[d.key];
              return (
                <div key={d.key} className="flex flex-wrap items-center gap-3 rounded-xl border border-surface-200 px-4 py-2.5" data-testid={`hours-${d.key}`}>
                  <span className="w-24 text-sm font-medium text-ink">{d.label}</span>
                  <label className="flex items-center gap-2 text-xs text-ink-soft"><Switch checked={!day.closed} onCheckedChange={(v) => setDay(d.key, "closed", !v)} data-testid={`hours-${d.key}-toggle`} />{day.closed ? "Closed" : "Open"}</label>
                  {!day.closed && (
                    <div className="flex items-center gap-2">
                      <Input type="time" value={day.open} onChange={(e) => setDay(d.key, "open", e.target.value)} className="h-9 w-32" data-testid={`hours-${d.key}-open`} />
                      <span className="text-ink-muted">–</span>
                      <Input type="time" value={day.close} onChange={(e) => setDay(d.key, "close", e.target.value)} className="h-9 w-32" data-testid={`hours-${d.key}-close`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Button type="submit" disabled={busy} className="h-11 rounded-full bg-brand px-6 text-white hover:bg-brand-600" data-testid="info-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes</Button>
      </form>

      <div className="kt-card mt-6 p-6" data-testid="gallery-section">
        <div className="mb-4 flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand"><Images className="h-5 w-5" /></span><div><h2 className="text-lg font-semibold text-ink">Storefront photos</h2><p className="text-sm text-ink-soft">Reorder to feature your best dishes. The first 3 appear on your storefront card (up to 20 total).</p></div></div>

        {(restaurant.gallery || []).length === 0 ? (
          <p className="mb-4 rounded-xl border border-surface-300 bg-surface-100 px-4 py-3 text-sm text-ink-soft" data-testid="gallery-empty">No photos yet — add your best dishes below so your storefront stands out.</p>
        ) : (
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="gallery-list">
            {(restaurant.gallery || []).map((g, idx) => (
              <div key={g} className="group relative aspect-square overflow-hidden rounded-xl border border-surface-200" data-testid="gallery-item">
                <img src={g} alt="Dish" className="h-full w-full object-cover" />
                {idx < 3 && <span className="absolute left-1.5 top-1.5 rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold text-white" data-testid="gallery-on-card">On card</span>}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-ink/80 to-transparent p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="flex gap-1">
                    <button type="button" onClick={() => movePhoto(idx, -1)} disabled={idx === 0} className="rounded-md bg-white/90 p-1 text-ink disabled:opacity-30" aria-label="Move left" data-testid={`gallery-move-up-${idx}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => movePhoto(idx, 1)} disabled={idx === (restaurant.gallery || []).length - 1} className="rounded-md bg-white/90 p-1 text-ink disabled:opacity-30" aria-label="Move right" data-testid={`gallery-move-down-${idx}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                  </div>
                  <button type="button" onClick={() => removePhoto(g)} className="rounded-md bg-white/90 p-1 text-red-700" aria-label="Remove photo" data-testid="gallery-remove"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {menu.filter((m) => m.image && !(restaurant.gallery || []).includes(m.image)).length > 0 && (
          <div className="mb-5" data-testid="gallery-from-menu">
            <p className="mb-2 text-sm font-medium text-ink-soft">Feature a dish from your menu</p>
            <div className="flex flex-wrap gap-2">
              {menu.filter((m) => m.image && !(restaurant.gallery || []).includes(m.image)).slice(0, 12).map((m) => (
                <button key={m.item_id || m.image} type="button" onClick={() => addPhoto(m.image)} className="group relative h-16 w-16 overflow-hidden rounded-lg border border-surface-200" title={m.name} data-testid="menu-photo-add">
                  <img src={m.image} alt={m.name} className="h-full w-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center bg-ink/40 opacity-0 transition-opacity group-hover:opacity-100"><Plus className="h-5 w-5 text-white" /></span>
                </button>
              ))}
            </div>
          </div>
        )}

        {(restaurant.gallery || []).length < 20 && (
          <div>
            <p className="mb-2 text-sm font-medium text-ink-soft">Add a photo (paste a URL or upload)</p>
            <div className="max-w-md space-y-3" data-testid="gallery-add">
              <ImageField value={photoUrl} onChange={setPhotoUrl} />
              <Button type="button" onClick={() => { addPhoto(photoUrl); setPhotoUrl(""); }} disabled={!photoUrl.trim()} className="rounded-full bg-brand px-5 text-white hover:bg-brand-600" data-testid="gallery-add-btn"><Plus className="mr-1.5 h-4 w-4" /> Add photo</Button>
            </div>
          </div>
        )}
      </div>

      {!orderOnlyManager && (
      <div className="kt-card mt-6 p-6" data-testid="kyc-section">
        <div className="mb-5 flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand"><ShieldCheck className="h-5 w-5" /></span><div><h2 className="text-lg font-semibold text-ink">Verification &amp; KYC</h2><p className="text-sm text-ink-soft">Upload your documents to get verified and listed to customers.</p></div></div>
        <KycStatus embedded />
      </div>
      )}

      <Dialog open={addrOpen} onOpenChange={setAddrOpen}>
        <DialogContent className="max-w-lg" data-testid="address-change-dialog">
          <DialogHeader><DialogTitle>Request address change</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-ink-soft">Set your new location below and upload a document proving the new address (e.g. a rent agreement or utility bill). Our team will review and approve it. Your current address stays live until then.</p>
            <div className="space-y-2">
              <Label>New address</Label>
              <Input value={addrForm.new_address} onChange={(e) => setAddrForm((f) => ({ ...f, new_address: e.target.value }))} data-testid="address-change-address" />
            </div>
            <div className="space-y-2">
              <Label>Pin the new location</Label>
              <LocationPicker
                value={{ lat: Number(addrForm.new_lat), lng: Number(addrForm.new_lng), address: addrForm.new_address }}
                onChange={({ lat, lng, address }) => setAddrForm((f) => ({ ...f, new_lat: lat, new_lng: lng, new_address: address || f.new_address }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Reason for the change</Label>
              <Textarea rows={2} value={addrForm.reason} onChange={(e) => setAddrForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. We've relocated to a bigger kitchen." data-testid="address-change-reason" />
            </div>
            <div className="space-y-2">
              <Label>Proof document</Label>
              <ImageField value={addrForm.proof_url} onChange={(v) => setAddrForm((f) => ({ ...f, proof_url: v }))} testId="address-change-proof" />
              <p className="text-xs text-ink-soft">A clear photo of your new rent agreement, utility bill or ownership document.</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setAddrOpen(false)} data-testid="address-change-cancel-dialog">Cancel</Button>
            <Button type="button" className="rounded-full bg-brand text-white hover:bg-brand-600" onClick={submitAddr} disabled={addrBusy} data-testid="address-change-submit">{addrBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
